import { ActionIcon, Avatar, Center, Group, Pagination, Table, Text, Tooltip } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDatasetListWithMembers } from '@mygpt/app/collaborate';
import { DatasetItemWithMembers, ShareSource } from '@mygpt/app/collaborate/services';
import { getResourcesPrinciple, ResourceDataType } from 'packages/app/apis/services/admin';

import { SvgEditNew } from '@/components/Common/icon';
import { LoadingSkeleton } from '@/components/Common/LoadingSkeleton';
import UserSelectorModal from './UserSelectorModal';

const DatasetList = ({ shareSource }: { shareSource?: ShareSource }) => {
  const { t } = useTranslation();

  const { data: datasetList, loadDatasetList, loading, page, setDatasetPage } = useDatasetListWithMembers();

  const [modalOpened, setModalOpened] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<DatasetItemWithMembers | null>(null);
  const [resourcePrincipleMap, setResourcePrincipleMap] = useState<Record<string, any[]>>({});

  const isLocalMode = import.meta.env.VITE_MODE === 'local';

  useEffect(() => {
    loadDatasetList({ page: 1 });
  }, []);

  useEffect(() => {
    if (!isLocalMode) return;

    const dataIds = datasetList.map((item: DatasetItemWithMembers) => item.dataset.id);

    if (dataIds.length) {
      getResourcesPrinciple({
        dataIds,
        dataType: 'DATASET' as ResourceDataType,
        authorityCode: 'DATASET_EDIT',
      }).then((res) => {
        const data = res.data || res;
        const map: Record<string, any[]> = {};
        const userMap = Object.fromEntries((data.users || []).map((u: any) => [u.id, u]));
        Object.entries(data.resourceDataIdToUserIdsMap || {}).forEach(([dataId, userIds]) => {
          map[dataId] = (userIds as string[]).map((uid) => userMap[uid]).filter(Boolean);
        });
        setResourcePrincipleMap(map);
      });
    }
  }, [isLocalMode, datasetList, page.page]);

  const handleEdit = (item: DatasetItemWithMembers) => {
    setSelectedItem(item);
    setModalOpened(true);
  };

  const handleModalClose = () => {
    setModalOpened(false);
    setSelectedItem(null);
  };

  const handleRefresh = () => {
    loadDatasetList({ page: page.page });
  };

  return (
    <>
      {loading ? (
        <LoadingSkeleton />
      ) : datasetList.length > 0 ? (
        <Table verticalSpacing="sm" horizontalSpacing="md" withColumnBorders borderColor="gray.1">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <Text>{t('access.table.title.dataset_name')}</Text>
              </Table.Th>
              <Table.Th className="max-w-[700px]">
                <Text>{t('access.table.title.desc')}</Text>
              </Table.Th>
              <Table.Th className="w-[180px]">
                <Text>{t('access.table.title.members')}</Text>
              </Table.Th>
              <Table.Th className="w-[100px]">
                <Text ta="center">{t('access.table.title.action')}</Text>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {datasetList.map((item: DatasetItemWithMembers, index: number) => {
              let members = item.members;
              const id = item.dataset.id;
              const name = item.dataset.name;
              const desc = item.dataset.description;

              if (isLocalMode && resourcePrincipleMap[id]) {
                members = resourcePrincipleMap[id];
              }

              return (
                <Table.Tr key={index} className="hover:bg-gray-50 transition-all">
                  <Table.Td>
                    <Tooltip
                      label={name}
                      position="bottom-start"
                      arrowOffset={10}
                      multiline
                      withArrow
                      className="max-w-[500px] break-all"
                    >
                      <Text c="gray.7" lineClamp={1} w={!isLocalMode ? 500 : 'auto'}>
                        {name}
                      </Text>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label={desc} multiline withArrow className="max-w-[500px] break-all">
                      <Text c="gray.7" lineClamp={1} w={!isLocalMode ? 500 : 'auto'}>
                        {desc}
                      </Text>
                    </Tooltip>
                  </Table.Td>
                  <Table.Td>
                    <Avatar.Group>
                      {members.map((memberItem: any, idx: number) => {
                        const user = memberItem.member || memberItem;
                        return (
                          idx < 7 && (
                            <Tooltip label={user.email} withArrow key={Math.random()}>
                              <Avatar key={user.user_id || user.id} src={user.picture} radius="xl" size="sm" />
                            </Tooltip>
                          )
                        );
                      })}
                      {members.length > 7 && (
                        <Avatar radius="xl" size="sm">
                          <span className="font-normal">+{members.length - 7}</span>
                        </Avatar>
                      )}
                    </Avatar.Group>
                  </Table.Td>
                  <Table.Td>
                    <Group justify="center">
                      <Tooltip label={t('access.table.edit')}>
                        <ActionIcon size="sm" variant="white" onClick={() => handleEdit(item)}>
                          <SvgEditNew />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      ) : (
        <p className="text-center mt-10 text-gray-5 text-lg">{t('access.nodata.dataset_desc')}</p>
      )}

      {page.pages > 1 && (
        <Center className="mt-0 py-3">
          <Pagination
            value={page.page}
            size="sm"
            onChange={(value) => setDatasetPage({ page: value })}
            total={page.pages}
            color="violet.5"
          />
        </Center>
      )}

      <UserSelectorModal
        opened={modalOpened}
        onClose={handleModalClose}
        selectedItem={selectedItem}
        type="dataset"
        onRefresh={handleRefresh}
        shareSource={shareSource}
      />
    </>
  );
};

export default DatasetList;
